import axios from "axios";
import { API } from "@/lib/api";

// Only the OPTIONAL live-account lookup gets a timeout. The authoritative
// /voice/cloned request is left EXACTLY like Clone My Voice (no timeout) so a
// slow reconciliation can never be cut off — that cutoff was the prod bug.
const ACCOUNT_TIMEOUT = 4000;

// Accept either an AxiosResponse ({ data: ... }) or an already-unwrapped
// payload, and either { voices: [...] } or a bare array.
function extractVoiceArray(res) {
  const payload = res && typeof res === "object" && "data" in res ? res.data : res;
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.voices)) return payload.voices;
  return [];
}

// Clone My Voice library (db.user_voices): { voice_id, name, is_default }
function mapLibrary(arr) {
  return arr
    .map((v) => ({ id: v.voice_id || v.id, name: v.name || "Voice", is_default: !!v.is_default }))
    .filter((v) => v.id);
}

// Live ElevenLabs account voices: { id, name, category }. Keep the user's own
// voices (clones/generated/professional); drop ElevenLabs "premade" stock.
function mapAccount(arr) {
  return arr
    .filter((v) => {
      const c = String(v.category || "").toLowerCase();
      return c === "" || c === "cloned" || c === "generated" || c === "professional";
    })
    .map((v) => ({ id: v.voice_id || v.id, name: v.name || "Voice", is_default: !!v.is_default }))
    .filter((v) => v.id);
}

function safeStatus(reason) {
  const code = reason && reason.response && reason.response.status;
  if (code) return `HTTP ${code}`;
  if (reason && reason.code === "ECONNABORTED") return "timeout";
  return "network error";
}

/**
 * AUTHORITATIVE cloned-voice source. Identical request to Clone My Voice:
 * `axios.get(`${API}/voice/cloned`)` on the shared client (auth via the global
 * interceptor), NO client-side timeout — let it resolve however long it takes.
 * Returns { voices, error } where error is set ONLY if this request fails.
 */
export async function fetchClonedLibrary() {
  try {
    const res = await axios.get(`${API}/voice/cloned`);
    return { voices: mapLibrary(extractVoiceArray(res)), error: null };
  } catch (e) {
    return {
      voices: [],
      error: `Couldn't load your cloned voices (${safeStatus(e)}). Check your connection or your ElevenLabs key in Clone My Voice, then reopen this panel.`,
    };
  }
}

/**
 * OPTIONAL supplemental source (same one the working ScriptStudio / AutoCreate
 * pickers use). Bounded by a short timeout and NEVER fatal — on any failure it
 * simply returns []. Must not affect the authoritative request or the error UI.
 */
export async function fetchAccountClones() {
  try {
    const res = await axios.get(`${API}/voice/elevenlabs/voices`, { timeout: ACCOUNT_TIMEOUT });
    return mapAccount(extractVoiceArray(res));
  } catch (_) {
    return [];
  }
}

/** Merge account + library clones by id; the library entry wins so we keep the
 *  friendly name + is_default flag (e.g. bb7/31 · Default). */
export function mergeCloneVoices(account, library) {
  const byId = new Map();
  (account || []).forEach((v) => { if (v.id) byId.set(v.id, v); });
  (library || []).forEach((v) => { if (v.id) byId.set(v.id, v); });
  return Array.from(byId.values());
}

/**
 * Decide which cloned voice to auto-select. Keeps the user's existing choice
 * if it's still available, otherwise the is_default clone (e.g. bb7/31), else
 * the first voice. Returns the voice object to select, or null to keep as-is.
 */
export function pickDefaultVoice(voices, currentVoiceId) {
  if (currentVoiceId && voices.some((v) => v.id === currentVoiceId)) return null;
  return voices.find((v) => v.is_default) || voices[0] || null;
}
